import { useEffect, useState } from 'react'
import { useTexte, WORTLAUT } from '../../texte'
import { Navigate } from 'react-router-dom'
import { rueckfrage } from '../../components/Rueckfrage'
import { Kontoloeschung } from './Kontoloeschung'

import {
  useMe,
  usePeople,
  useSetDisabled,
  useSetPersonName,
  useSetRole,
  useSetSupervisor,
} from '../../hooks/queries'
import type { AccountRole, Person } from '../../api/types'

/**
 * Wer darf eigene Reiseberichte schreiben.
 *
 * Sichtbar nur für die Verwaltung — für alle anderen wäre diese Liste ein
 * Verzeichnis aller Konten. Der Server verweigert sie ohnehin; hier wird sie
 * gar nicht erst angefragt.
 */
/**
 * Der alte Weg hierher.
 *
 * Seit #39 sitzt die Personenliste als Kategorie in der Administration. Die Route
 * bleibt trotzdem bestehen: sie steht in Lesezeichen, in älteren Verweisen und
 * in einem Browserlauf. Ein Weg, den es gab, führt weiter hin -- nur eben
 * dorthin, wo die Sache jetzt steht.
 */
export function PeoplePage() {
  return <Navigate to="/settings/administration?bereich=personen" replace />
}

/**
 * Die Personenliste selbst -- **ohne Kopfzeile und ohne Scrollrahmen**.
 *
 * Beides bringt seit #39 die Kategorienansicht mit. Stünde es hier noch
 * einmal, gäbe es zwei Kopfzeilen übereinander und zwei Bereiche, die
 * unabhängig voneinander scrollen.
 */
export function Personenliste() {
  const t = useTexte()
  const me = useMe()
  const darfVerwalten = me.data?.may_manage_people === true
  const people = usePeople(darfVerwalten)
  const setRole = useSetRole()
  const setDisabled = useSetDisabled()
  const setName = useSetPersonName()
  const setSupervisor = useSetSupervisor()
  // Nur ein Supervisor gibt das Merkmal weiter -- der Server entscheidet das
  // (`requireSupervisor`); hier wird das Häkchen nur nicht angeboten.
  const darfSupervisor = me.data?.may_operate === true
  /*
   * **Was gelöscht wurde, steht über der Liste und nicht in der Zeile.**
   *
   * Der Satz stand bis hierher in der Zeile der gelöschten Person -- und die
   * verschwindet im selben Augenblick, weil die Liste neu lädt. Er war damit
   * für einen Wimpernschlag da oder gar nicht; der Browserlauf
   * (`kontoloeschen.mjs`) wartete fünfzehn Sekunden auf ihn und lief in eine
   * Zeitüberschreitung. Eine Meldung, die man nicht lesen kann, ist keine.
   *
   * Er bleibt stehen, bis das nächste Konto gelöscht wird: was weg ist, ist
   * weg, und die Zahlen dazu will man in Ruhe nachlesen können.
   */
  const [geloescht, setGeloescht] = useState<string | null>(null)

  return (
    <>
        <div className="card card--seite">
          <p className="card__lead" style={{ marginBottom: 0 }}>{t.personen.lead}</p>
        </div>

        {(setRole.isError || setDisabled.isError || setName.isError || setSupervisor.isError) && (
          <p className="notice" role="alert" style={{ maxWidth: 520, margin: '0 auto 16px' }}>
            {(setRole.error ?? setDisabled.error ?? setName.error ?? setSupervisor.error)?.message}
          </p>
        )}

        {geloescht !== null && (
          <p
            className="notice"
            role="status"
            style={{ maxWidth: 520, margin: '0 auto 16px' }}
          >
            {geloescht}
          </p>
        )}

        <div className="card card--seite" style={{ marginBottom: 0 }}>
          {people.isPending && <p className="card__lead">{t.personen.wirdGeladen}</p>}

          <div className="stack">
            {people.data?.map((person) => (
              <PersonRow
                key={person.id}
                person={person}
                self={person.id === me.data?.id}
                busy={
                  setRole.isPending ||
                  setDisabled.isPending ||
                  setName.isPending
                }
                onChange={(role) => setRole.mutate({ id: person.id, role })}
                onDisabled={(disabled) => setDisabled.mutate({ id: person.id, disabled })}
                onName={(name) => setName.mutate({ id: person.id, name })}
                onGeloescht={setGeloescht}
                darfSupervisor={darfSupervisor}
                onSupervisor={(supervisor) => setSupervisor.mutate({ id: person.id, supervisor })}
              />
            ))}
          </div>
        </div>
    </>
  )
}

function PersonRow({
  person,
  self,
  busy,
  onChange,
  onDisabled,
  onName,
  onGeloescht,
  darfSupervisor,
  onSupervisor,
}: {
  person: Person
  self: boolean
  busy: boolean
  onChange: (role: AccountRole) => void
  onDisabled: (disabled: boolean) => void
  onName: (name: string) => void
  /** Was gelöscht wurde -- gezeigt wird es über der Liste, nicht hier. */
  onGeloescht: (befund: string) => void
  /** Ob der Betrachter Supervisor ist und das Merkmal vergeben darf (#62). */
  darfSupervisor: boolean
  onSupervisor: (supervisor: boolean) => void
}) {
  const t = useTexte()
  // Der Entwurf bleibt im Feld, bis er hinausgeht. Ein Feld, das bei jedem
  // Buchstaben sichert, ist derselbe Fehler wie beim Regler des Deckgrads.
  const [entwurf, setEntwurf] = useState(person.admin_display_name ?? '')
  useEffect(() => {
    setEntwurf(person.admin_display_name ?? '')
  }, [person.admin_display_name])

  const angezeigt = person.admin_display_name ?? person.display_name

  const sichere = () => {
    const wert = entwurf.trim()
    if (wert === (person.admin_display_name ?? '')) return
    onName(wert)
  }

  return (
    <div
      className="stack"
      style={{ gap: 6, paddingBottom: 12, borderBottom: '1px solid var(--border)' }}
    >
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <span style={{ fontWeight: 600 }}>
          {angezeigt}
          {self && (
            <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>{t.personen.du}</span>
          )}
          {person.disabled && (
            <span style={{ color: 'var(--danger)', fontWeight: 400 }}>
              {t.personen.stillgelegt}
            </span>
          )}
          {person.supervisor === true && (
            <span style={{ color: 'var(--text-muted)', fontWeight: 400 }} data-person-supervisor="">
              {t.personen.supervisorMarke}
            </span>
          )}
        </span>
        <span className="feld__hinweis feld__hinweis--13">
          {person.trips > 0 ? t.personen.reisen(person.trips) : ''}
        </span>
      </div>

      {/* Beide Namen stehen da, sobald sie sich unterscheiden -- sonst wüsste
          man nicht mehr, wie dieser Mensch sich selbst nennt, und suchte ihn
          in seiner eigenen Antwort vergeblich. */}
      {person.admin_display_name !== null && (
        <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
          {t.personen.nenntSichSelbst(person.display_name)}
        </span>
      )}

      <span style={{ fontSize: 13, color: 'var(--text-muted)', wordBreak: 'break-all' }}>
        {person.email}
      </span>

      {/* **Nicht abgeblendet, solange irgendwo gesichert wird.** Eine
          Vorsichtsregel, die eine Bedienung überstimmt, ist ein kaputter
          Knopf: das Feld verlöre den Fokus mitten im Tippen, und auf dem
          Telefon ginge die Tastatur zu. Gesichert wird beim Verlassen, und
          zwei Anfragen hintereinander schaden hier nichts. */}
      <label className="field" style={{ marginBottom: 0 }}>
        <span className="visually-hidden">
          Name für die Verwaltung von {person.display_name}
        </span>
        {/* **Kein Vorschlag aus den Kontakten** (#28). Aus dem Betrieb: über
            der Tastatur stand „Kontakt autom. ausfüllen" -- iOS hält ein Feld
            mit einem Namen darin für ein Kontaktfeld und bietet die eigene
            Adresskartei an. Hier steht aber ein Name, den die *Verwaltung*
            vergibt; ein Vorschlag aus den eigenen Kontakten trifft nie zu.

            Die Leiste selbst bleibt: sie gehört der Tastatur und nicht der
            Seite, und keine Web-Anwendung kann sie ausblenden. Weg ist nur
            der Vorschlag darin.

            `autocorrect`/`autocapitalize` stehen dabei: ein selbst vergebener
            Name ist kein Fließtext, und eine Autokorrektur, die „Binder" zu
            „Binde" macht, ist hier keine Hilfe. */}
        <input
          className="field__input"
          type="text"
          maxLength={80}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="words"
          spellCheck={false}
          placeholder={t.personen.namensfeld(person.display_name)}
          value={entwurf}
          onChange={(event) => setEntwurf(event.target.value)}
          onBlur={sichere}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault()
              event.currentTarget.blur()
            }
          }}
        />
      </label>

      {self ? (
        // Die eigene Rolle lässt sich nicht ändern -- sonst könnte sich die
        // letzte Verwaltung selbst aussperren.
        <span className="feld__hinweis feld__hinweis--13">
          {beschreibung(person.role)} · {t.personen.eigeneRolle}
          {darfSupervisor && ` · ${t.personen.supervisorSelbst}`}
        </span>
      ) : (
        <>
          <label className="field" style={{ marginBottom: 0 }}>
            <span className="visually-hidden">{t.personen.rolleVon(person.display_name)}</span>
            <select
              className="field__input"
              value={person.role}
              disabled={busy || person.disabled}
              onChange={(event) => onChange(event.target.value as AccountRole)}
            >
              <option value="reader">{t.personen.liestMit}</option>
              <option value="author">{t.personen.darfReisen}</option>
              <option value="admin">{t.personen.darfVerwalten}</option>
            </select>
          </label>

          {/* Das Merkmal des Supervisors (#62): nur an Admins, nur vergeben
              von einem Supervisor. Ein Häkchen, kein vierter Eintrag in der
              Rollenliste -- es liegt quer zur Leiter. */}
          {darfSupervisor && person.role === 'admin' && (
            <label className="row row--wahl" style={{ gap: 10 }}>
              <input
                type="checkbox"
                className="wahl__haken"
                checked={person.supervisor === true}
                disabled={busy || person.disabled}
                onChange={(event) => onSupervisor(event.target.checked)}
                data-person-supervisor-haken=""
              />
              <span>
                <span style={{ fontWeight: 600 }}>{t.personen.supervisor}</span>
                <span className="feld__hinweis feld__hinweis--13" style={{ display: 'block' }}>
                  {t.personen.supervisorHinweis}
                </span>
              </span>
            </label>
          )}

          <button
            type="button"
            className="button button--outline"
            style={{ padding: '6px 12px', fontSize: 14 }}
            disabled={busy}
            onClick={async () => {
              // Freigeben braucht keine Rückfrage, Stilllegen schon -- und die
              // Frage soll sagen, was es nicht tut: Reisen und Nachrichten
              // bleiben.
              if (
                person.disabled ||
                (await rueckfrage(
                  t.personen.stilllegenFrage(person.display_name),
                  t.personen.stilllegenKurz,
                ))
              ) {
                onDisabled(!person.disabled)
              }
            }}
          >
            {person.disabled ? t.personen.freigeben : t.personen.stilllegen}
          </button>

          {/*
              **Der Knopf wird nicht mehr versteckt (#47).**

              Hier stand „Löschen nur, wo nichts dranhängt" -- und wo etwas
              dranhing, stand gar nichts, und niemand wusste warum. Seit #47
              lässt sich auch ein Konto mit Nachrichten und Fotos löschen, mit
              gezählter Vorschau und abgetippter E-Mail. Die eine Sperre, die
              bleibt, erklärt sich in der Vorschau selbst: wem eine Reise
              gehört, dessen Konto nicht.

              Nur das eigene bleibt ganz außen vor -- dafür gibt es keinen
              Zustand, in dem es sinnvoll wäre.
          */}
          {!self && <Kontoloeschung person={person} onGeloescht={onGeloescht} />}
        </>
      )}
    </div>
  )
}

function beschreibung(role: AccountRole): string {
  // Ohne Komponente gibt es keinen Haken -- die dokumentierte Ausnahme.
  if (role === 'admin') return WORTLAUT.personen.darfVerwalten
  if (role === 'author') return WORTLAUT.personen.darfReisen
  return WORTLAUT.personen.liestMit
}
